import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await apiClient.get(`/auth/session?t=${Date.now()}`);
      setUser(response.data.user);
      return response.data.user;
    } catch {
      setUser(null);
      return null;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('token');

        if (tokenFromUrl) {
          await apiClient.post('/auth/exchange-token', null, {
            headers: { Authorization: `Bearer ${tokenFromUrl}` }
          });
          urlParams.delete('token');
          urlParams.delete('email');
          const search = urlParams.toString() ? `?${urlParams.toString()}` : '';
          window.history.replaceState({}, document.title, `${window.location.pathname}${search}${window.location.hash}`);
        }

        const authenticatedUser = await checkAuth();
        if (!isMounted && authenticatedUser) return;
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();
    return () => { isMounted = false; };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      setUser(response.data.user);
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message, details: error.response?.data?.details };
    }
  };

  const register = async (userData) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      setUser(response.data.user);
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message, details: error.response?.data?.details };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout');
      toast.success('Logged out successfully');
    } catch {
      toast.error('Signed out locally; the server could not be reached.');
    } finally {
      setUser(null);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await apiClient.put('/auth/profile', profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message, details: error.response?.data?.details };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await apiClient.put('/auth/password', passwordData);
      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message, details: error.response?.data?.details };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, changePassword, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
